import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserService } from '@vsolv/core/users/web';
import { Payment } from '@vsolv/packages/payments/domain';
import { firstValueFrom } from 'rxjs';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const Accept: any;

export interface PaymentMethodServiceConfig {
  gatewayInstanceId: string | null;
  apiLoginID: string | null;
  clientKey: string | null;
}

@Injectable()
export class PaymentMethodService {
  constructor(
    private userSvc: UserService,
    private http: HttpClient
  ) {}

  list(dto: Payment.ListPaymentMethodsRequest) {
    return firstValueFrom(
      this.http.get<Payment.ListPaymentMethodsResponse>(`/api/payment-methods${dto.forCheckout ? '/checkout' : ''}`, {
        params: {
          ...(dto.page ? { page: dto.page } : {}),
          ...(dto.limit ? { limit: dto.limit } : {}),
          ...(dto.userId ? { userId: dto.userId } : {}),
          ...(dto.gatewayInstanceId ? { gatewayInstanceId: dto.gatewayInstanceId } : {}),
        },
      })
    );
  }

  async retrieveConfig(gatewayInstanceId?: string) {
    const value = await firstValueFrom(
      this.http.get<PaymentMethodServiceConfig>(`/api/payment-methods/retrieve-service-config`, {
        params: gatewayInstanceId ? { gatewayInstanceId } : {},
      })
    );
    return value;
  }

  async addCreditCard({
    cardData,
    gatewayInstanceId,
    ...dto
  }: Omit<Payment.AddCreditCardRequest, 'creditCardNonce'> & {
    cardData: { cardNumber: string; month: string; year: string; cardCode: string; zip?: string; fullName?: string };
  }) {
    const tokenizedCard = await this.exchangeCreditCardNonce(cardData, gatewayInstanceId);
    return await firstValueFrom(
      this.http.post<Payment.AddCreditCardResponse>(`/api/payment-methods`, {
        ...dto,
        gatewayInstanceId: tokenizedCard.gatewayInstanceId ?? undefined,
        creditCardNonce: tokenizedCard.creditCardNonce,
      })
    );
  }

  async addBankAccount(dto: Payment.AddBankAccountRequest) {
    return await firstValueFrom(
      this.http.post<Payment.AddBankAccountResponse>(`/api/payment-methods/bank-account`, dto)
    );
  }

  private async exchangeCreditCardNonce(
    cardData: {
      cardNumber: string;
      month: string;
      year: string;
      cardCode: string;
      zip?: string;
      fullName?: string;
    },
    gatewayInstanceId?: string
  ) {
    const {
      gatewayInstanceId: resolvedGatewayInstanceId,
      apiLoginID,
      clientKey,
    } = await this.retrieveConfig(gatewayInstanceId);

    const authData = { apiLoginID, clientKey };

    return new Promise<{ creditCardNonce: string; gatewayInstanceId: string | null }>((res, rej) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Accept.dispatchData({ authData, cardData }, (response: any) => {
        if (response.messages.resultCode === 'Error') {
          rej(new Error('The payment method could not be tokenized. Check its details and try again.'));
          return;
        }
        res({ creditCardNonce: response.opaqueData.dataValue, gatewayInstanceId: resolvedGatewayInstanceId });
      });
    });
  }
}
